import React, { useState, useRef } from 'react';

interface CsvImportModalProps {
  apiBaseUrl: string;
  csrfToken: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({
  apiBaseUrl,
  csrfToken,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    imported_count: number;
    updated_count: number;
    errors: string[];
    message: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      alert('CSVファイルを選択してください');
      return;
    }

    setIsSubmitting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${apiBaseUrl}/import_csv`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok || response.status === 207) {
        setResult(data);
        if (data.success && data.errors.length === 0) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        setResult({
          success: false,
          imported_count: 0,
          updated_count: 0,
          errors: data.errors || ['インポートに失敗しました'],
          message: 'インポートに失敗しました',
        });
      }
    } catch (error) {
      console.error('CSV import failed:', error);
      setResult({
        success: false,
        imported_count: 0,
        updated_count: 0,
        errors: ['インポートに失敗しました'],
        message: 'インポートに失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `primary_type,primary_id,secondary_type,secondary_id,slug,published,display_priority
Skill,1,Occupation,5,react-frontend-engineer,true,100
Skill,1,Characteristic,3,react-remote,false,90`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'cross_landing_pages_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex={-1}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">CSVインポート</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="閉じる"
            />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">
                  CSVファイル <span className="text-danger">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control"
                  accept=".csv"
                  onChange={handleFileChange}
                  required
                />
                <small className="form-text text-muted">
                  CSVファイルを選択してください。UTF-8エンコーディングを推奨します。
                </small>
              </div>

              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleDownloadTemplate}
                >
                  テンプレートをダウンロード
                </button>
              </div>

              <div className="mb-3">
                <h6>CSVフォーマット</h6>
                <p className="small text-muted mb-2">
                  必須カラム: primary_type, primary_id, secondary_type, secondary_id
                </p>
                <p className="small text-muted mb-2">
                  任意カラム: slug, published (true/false), display_priority
                </p>
                <p className="small text-info mb-2">
                  ※ title, h1, meta_descriptionは自動生成されます
                </p>
                <pre className="bg-light p-2 rounded small" style={{ fontSize: '11px' }}>
{`primary_type,primary_id,secondary_type,secondary_id,slug,published,display_priority
Skill,1,Occupation,5,react-frontend-engineer,true,100`}
                </pre>
              </div>

              {result && (
                <div className={`alert ${result.success ? 'alert-success' : 'alert-warning'}`}>
                  <h6>{result.message}</h6>
                  <p className="mb-1">
                    新規作成: {result.imported_count}件 / 更新: {result.updated_count}件
                  </p>
                  {result.errors.length > 0 && (
                    <div className="mt-2">
                      <strong>エラー詳細:</strong>
                      <ul className="mb-0 mt-1">
                        {result.errors.map((error, index) => (
                          <li key={index} className="small">{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                閉じる
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting || !file}>
                {isSubmitting ? 'インポート中...' : 'インポート'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;

